/**
 * AskX1Widget.tsx — bottom-right "Ask X1" chat widget.
 * Mounted globally in src/App.tsx. Talks to POST /api/chat (Route B backend).
 *
 * Page-aware: when the user is on an entity page (wallet/validator/token/tx/
 * nft/program), the widget reads that context from AskX1ContextProvider, shows
 * a "Viewing:" chip, offers one-tap quick-action questions, and attaches the
 * context to every message so "this"/"here" resolves server-side.
 */
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { apiService } from '@/services/api'
import { useAskX1Context, type AskX1EntityType } from './AskX1ContextProvider'
import { AskX1Chart, type ChartPayload } from './AskX1Chart'
import { AskX1Markdown } from './AskX1Markdown'

// x1-mascot.js (served from public/) attaches this global. It's the animated
// 3-layer mascot (body + waving arm + blinking eyes) that acts as Forti's launcher.
declare global {
  interface Window {
    X1Mascot?: {
      init(opts: Record<string, unknown>): unknown
      pause(): unknown
      resume(): unknown
      destroy(): unknown
    }
  }
}

type EntityAction = { label: string; kind: 'link' | 'query'; target: string }
type Entity = {
  type: 'token' | 'validator' | 'wallet' | 'transaction' | 'nft' | 'program' | 'pool'
  id: string
  label: string
  href: string
  icon?: string | null
  stats?: { marketCap?: number | null; liquidity?: number | null; supply?: number | null; priceUsd?: number | null; stakeXnt?: number | null; commission?: number | null; score?: number | null } | null
  group?: string | null
  actions: EntityAction[]
}
type Msg = { role: 'user' | 'assistant'; content: string; typing?: boolean; chart?: ChartPayload | null; entities?: Entity[] | null; sources?: { id: string; label: string }[] | null; suggestions?: string[] | null }

// --- SSE + reveal helpers (backend streams token/sources/suggestions/entities/chart/done) ---
function parseSSE(raw: string): { event: string; data: any } {
  let event = ''
  let dataStr = ''
  for (const line of raw.split('\n')) {
    if (line.startsWith('event:')) event = line.slice(6).trim()
    else if (line.startsWith('data:')) dataStr += line.slice(5).trim()
  }
  let data: any = null
  if (dataStr) { try { data = JSON.parse(dataStr) } catch { data = null } }
  return { event, data }
}
// The upstream shim buffers, so tokens arrive together — reveal client-side, terminal style.
// Strip Markdown to clean display text for the typewriter reveal, so the user never watches raw
// **bold**, `code`, headings, or 44-char /validators/<pubkey> URLs type out. Links -> their label
// (URL dropped); the FINAL render still uses the raw Markdown (clickable links) via AskX1Markdown.
function stripMarkdown(md: string): string {
  return (md || '')
    .replace(/!\[[^\]]*\]\([^)]*\)/g, '')        // images -> drop
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')      // [label](url) -> label (no URL)
    .replace(/`([^`]+)`/g, '$1')                  // `code` -> code
    .replace(/^\s{0,3}#{1,6}\s+/gm, '')           // # headings -> text
    .replace(/(\*\*|__)(.*?)\1/g, '$2')           // **bold** -> text
    .replace(/(\*|_)([^*_\n]+)\1/g, '$2')         // *italic* -> text
    .replace(/^\s*[-*+]\s+/gm, '• ')              // - bullets -> •
    .replace(/^\s*>\s?/gm, '')                    // > blockquote markers
}
// Char-by-char for short replies; accelerates for long ones so the WHOLE reveal caps at ~1.8s.
function typewriter(text: string, onStep: (shown: string) => void): Promise<void> {
  const reduced = typeof window !== 'undefined' && window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  return new Promise(resolve => {
    if (reduced || !text) { onStep(text); resolve(); return } // no animation for reduced-motion
    const frames = Math.min(text.length, 120)   // ~120 frames × 15ms ≈ 1.8s max, no matter the length
    const step = Math.max(1, Math.ceil(text.length / frames))
    let i = 0
    const iv = setInterval(() => {
      i = Math.min(text.length, i + step)
      onStep(text.slice(0, i))
      if (i >= text.length) { clearInterval(iv); resolve() }
    }, 15)
  })
}
// Old-school terminal line: clean (markdown-stripped) text in monospace with a blinking cursor.
function TypingLine({ text }: { text: string }) {
  return (
    <span className="whitespace-pre-wrap break-words font-mono text-[13px] leading-relaxed text-secondary-800 dark:text-secondary-100">
      {text}<span className="forti-caret" aria-hidden>▋</span>
    </span>
  )
}
const LOADER_STEPS = ['Searching the blockchain…', 'Reading on-chain data…', 'Analyzing…']
function StreamLoader() {
  const [i, setI] = useState(0)
  useEffect(() => {
    const iv = setInterval(() => setI(x => (x + 1) % LOADER_STEPS.length), 1200)
    return () => clearInterval(iv)
  }, [])
  return (
    <div className="flex items-center gap-2 text-xs text-secondary-400">
      <span className="inline-flex gap-1">
        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-primary-400" />
        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-primary-400 [animation-delay:150ms]" />
        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-primary-400 [animation-delay:300ms]" />
      </span>
      {LOADER_STEPS[i]}
    </div>
  )
}

function fmtUsd(n?: number | null): string | null {
  if (n == null || !Number.isFinite(n) || n <= 0) return null
  if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`
  if (n >= 1e3) return `$${(n / 1e3).toFixed(1)}K`
  return `$${n.toFixed(0)}`
}

// Icon avatar — reuses the explorer's own logo/identity URL; onError → monogram fallback
// (same graceful-degrade pattern as TokenCell), so a dead image never breaks the card.
function EntityIcon({ icon, label }: { icon?: string | null; label: string }) {
  const [failed, setFailed] = useState(false)
  const mono = (label || '?').trim().slice(0, 1).toUpperCase()
  if (!icon || failed) {
    return (
      <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-secondary-200 text-[10px] font-semibold text-secondary-600 dark:bg-secondary-700 dark:text-secondary-300">
        {mono}
      </div>
    )
  }
  return <img src={icon} alt="" onError={() => setFailed(true)} className="h-6 w-6 flex-shrink-0 rounded-full object-cover" />
}

// One interactive entity card: icon + linked name + key stat + action buttons.
function EntityCard({ entity, onNavigate, onQuery, busy }: {
  entity: Entity
  onNavigate: (href: string) => void
  onQuery: (q: string) => void
  busy: boolean
}) {
  const stat = fmtUsd(entity.stats?.liquidity) || fmtUsd(entity.stats?.marketCap)
  const statLabel = fmtUsd(entity.stats?.liquidity) ? 'liq' : (fmtUsd(entity.stats?.marketCap) ? 'mcap' : '')
  // Validators show stake · commission · score on their own line (cards are the whole answer now).
  const s = entity.stats
  const valBits = entity.type === 'validator' && s ? [
    s.stakeXnt != null ? `${s.stakeXnt.toLocaleString('en-US')} XNT` : null,
    s.commission != null ? `${s.commission}% commission` : null,
    s.score != null ? `score ${s.score}` : null,
  ].filter(Boolean).join(' · ') : ''
  return (
    <div className="mt-1 w-full min-w-0 rounded-xl border border-secondary-200 bg-white px-2.5 py-2 dark:border-secondary-700 dark:bg-secondary-900">
      <div className="flex min-w-0 items-center gap-2">
        <EntityIcon icon={entity.icon} label={entity.label} />
        <button onClick={() => onNavigate(entity.href)}
          className="min-w-0 truncate text-left text-sm font-semibold text-primary-600 hover:underline dark:text-primary-400">
          {entity.label}
        </button>
        <span className="flex-shrink-0 text-[11px] text-secondary-400" title={entity.id}>{shortId(entity.id)}</span>
        {stat && <span className="ml-auto flex-shrink-0 text-[11px] font-medium text-secondary-500 dark:text-secondary-400">{stat} {statLabel}</span>}
      </div>
      {valBits && <div className="mt-1 text-[11px] font-medium text-secondary-500 dark:text-secondary-400">{valBits}</div>}
      <div className="mt-1.5 flex flex-wrap gap-1">
        {entity.actions.map(a => (
          <button key={a.label} disabled={busy}
            onClick={() => (a.kind === 'link' ? onNavigate(a.target) : onQuery(a.target))}
            className="rounded-md border border-secondary-300 bg-secondary-50 px-2 py-0.5 text-[11px] text-secondary-700 transition hover:border-primary-400 hover:text-primary-700 disabled:opacity-50 dark:border-secondary-600 dark:bg-secondary-800 dark:text-secondary-200 dark:hover:border-primary-500 dark:hover:text-primary-300">
            {a.label}
          </button>
        ))}
      </div>
    </div>
  )
}

function sessionId(): string {
  const KEY = 'askx1_session_id'
  let id = localStorage.getItem(KEY)
  if (!id) {
    id = crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`
    localStorage.setItem(KEY, id)
  }
  return id
}

const GREETING: Msg = {
  role: 'assistant',
  content: 'Hi, I’m Forti — your X1 Blockchain companion. Ask me about validators, tokens, staking, NFTs, the network, or anything on the explorer. I’m read-only and explain on-chain data (not financial advice).',
}

// Swirling conic-gradient glow around the OPEN panel. @property drives a smooth
// GPU-friendly rotation of the gradient angle; degrades to a static gradient ring
// where @property is unsupported, and freezes under prefers-reduced-motion.
const FORTI_SWIRL_CSS = `
@property --forti-a { syntax: '<angle>'; initial-value: 0deg; inherits: false; }
@keyframes forti-spin { to { --forti-a: 360deg; } }
.forti-swirl {
  background: conic-gradient(from var(--forti-a), #22d3ee, #6366f1, #a855f7, #22c55e, #22d3ee);
  animation: forti-spin 6s linear infinite;
  will-change: --forti-a;
}
@supports not (background: conic-gradient(from var(--forti-a))) {
  .forti-swirl { background: linear-gradient(120deg, #22d3ee, #a855f7, #22c55e); animation: none; }
}
@media (prefers-reduced-motion: reduce) { .forti-swirl { animation: none; } }
@keyframes forti-caret { 0%,49% { opacity: 1 } 50%,100% { opacity: 0 } }
.forti-caret { display: inline-block; margin-left: 1px; color: #22c55e; font-weight: 700; animation: forti-caret 1s step-end infinite; }
@media (prefers-reduced-motion: reduce) { .forti-caret { animation: none; opacity: 1; } }
`

// One-tap questions per entity type. The page context resolves "this".
const QUICK_ACTIONS: Record<AskX1EntityType, string[]> = {
  wallet: ['Summarize this wallet', 'Largest transfers', 'Token holdings', 'Staking & delegations', 'Risk / behavior'],
  validator: ['Explain validator health', 'Performance & skip rate', 'Rewards', 'Stake trend', 'Commission history'],
  transaction: ['Explain this transaction', 'Fee breakdown', 'Balance changes', 'Programs used'],
  token: ['Tokenomics', 'Top holders', 'Liquidity & markets', 'Whale movements', 'Price history'],
  nft: ['About this collection', 'Why is it popular', 'Traits & rarity'],
  program: ['What does this program do', 'Recent activity', 'Top callers'],
}

function shortId(id: string): string {
  return id.length > 12 ? `${id.slice(0, 4)}…${id.slice(-4)}` : id
}

// Truncate long base58 strings (32–44 pubkeys/mints, 64–88 tx sigs) in DISPLAY
// text so they never overflow the bubble. The base58 alphabet excludes 0/O/I/l,
// so ordinary words won't match. Applied to the rendered string only.
function truncateAddrs(t: string): string {
  return t.replace(/[1-9A-HJ-NP-Za-km-z]{32,88}/g, s => `${s.slice(0, 4)}…${s.slice(-4)}`)
}

export function AskX1Widget() {
  const { pageContext } = useAskX1Context()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [input, setInput] = useState('')
  const [busy, setBusy] = useState(false)
  const [messages, setMessages] = useState<Msg[]>([GREETING])
  const scrollRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const [trending, setTrending] = useState<string[]>([])
  const trendingFetched = useRef(false)

  useEffect(() => { scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight) }, [messages, open])
  useEffect(() => { if (open) inputRef.current?.focus() }, [open])

  // Click/tap outside the open panel closes it. The mascot launcher is a separate
  // element that's hidden while open, so it won't re-trigger. pointerdown covers mouse + touch.
  useEffect(() => {
    if (!open) return
    const onDown = (e: PointerEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('pointerdown', onDown)
    return () => document.removeEventListener('pointerdown', onDown)
  }, [open])

  // Trending searches (abuse-resistant, per-IP-deduped server-side). Fetched once on
  // first open; shown as clickable chips only in the opening state. Hidden if empty/errors.
  useEffect(() => {
    if (!open || trendingFetched.current) return
    trendingFetched.current = true
    fetch(apiService.buildUrl('/api/chat/trending') + '?limit=3')
      .then(r => (r.ok ? r.json() : null))
      .then(d => {
        const list = Array.isArray(d?.trending) ? d.trending : []
        const qs = list.map((x: any) => (typeof x?.query === 'string' ? x.query.trim() : '')).filter(Boolean)
        setTrending(qs.slice(0, 3))
      })
      .catch(() => { /* silent — no trending row */ })
  }, [open])

  // Load + init the animated mascot (x1-mascot.js) as Forti's launcher: clicking
  // it opens the chat. Self-contained script served from public/. Init once.
  const mascotInited = useRef(false)
  useEffect(() => {
    let cancelled = false
    function initMascot() {
      if (cancelled || mascotInited.current || !window.X1Mascot) return
      mascotInited.current = true
      window.X1Mascot.init({
        onClick: () => setOpen(true),
        side: 'right',
        offsetX: 20,
        offsetY: 14,
        size: (typeof window !== 'undefined' && window.innerWidth < 640) ? 61 : 77, // ~20% smaller; smaller still on mobile
        liveliness: 4,
        greetOnLoad: false,  // no wave-on-load
        blink: true,
        bubble: '',          // no "Need a hand?" speech bubble
        // Arm-wave disabled per request — drop "wave"; keep idle float/blink + calm gestures.
        gestures: ['hop', 'wiggle', 'look', 'lean', 'excited', 'blink'],
      })
    }
    if (window.X1Mascot) {
      initMascot()
    } else {
      let s = document.getElementById('x1-mascot-script') as HTMLScriptElement | null
      if (!s) {
        s = document.createElement('script')
        s.id = 'x1-mascot-script'
        s.src = '/x1-mascot.js'
        s.async = true
        document.body.appendChild(s)
      }
      s.addEventListener('load', initMascot)
    }
    return () => {
      cancelled = true
      try { window.X1Mascot?.destroy() } catch { /* ignore */ }
      mascotInited.current = false
    }
  }, [])

  // The open panel would sit under the mascot (its z-index is huge), so hide +
  // pause the mascot while the chat is open; show + resume it when closed.
  useEffect(() => {
    const root = document.getElementById('x1-mascot-root') as HTMLElement | null
    if (open) {
      window.X1Mascot?.pause()
      if (root) root.style.display = 'none'
    } else {
      if (root) root.style.display = ''
      window.X1Mascot?.resume()
    }
  }, [open])

  async function send(overrideText?: string) {
    const text = (overrideText ?? input).trim()
    if (!text || busy) return
    if (!overrideText) setInput('')
    const history = messages.filter(m => m !== GREETING).slice(-10)
    setMessages(m => [...m, { role: 'user', content: text }])
    setBusy(true)
    const updateLastAsst = (patch: Partial<Msg>) =>
      setMessages(m => {
        const c = [...m]
        for (let i = c.length - 1; i >= 0; i--) {
          if (c[i].role === 'assistant') { c[i] = { ...c[i], ...patch }; break }
        }
        return c
      })

    try {
      const body: Record<string, unknown> = { message: text, sessionId: sessionId(), history }
      if (pageContext) body.context = pageContext
      const res = await fetch(apiService.buildUrl('/api/chat') + '?stream=1', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
        body: JSON.stringify(body),
      })
      const ct = res.headers.get('Content-Type') || ''

      let full = ''
      let sources: Msg['sources'] = null
      let suggestions: Msg['suggestions'] = null
      let entities: Entity[] | null = null
      let chart: ChartPayload | null = null

      if (res.ok && ct.includes('text/event-stream') && res.body) {
        // ---- SSE path: read the stream, then typewriter-reveal ----
        const reader = res.body.getReader()
        const dec = new TextDecoder()
        let buf = ''
        for (;;) {
          const { value, done } = await reader.read()
          if (done) break
          buf += dec.decode(value, { stream: true })
          let idx: number
          while ((idx = buf.indexOf('\n\n')) >= 0) {
            const rawEv = buf.slice(0, idx); buf = buf.slice(idx + 2)
            const { event, data } = parseSSE(rawEv)
            if (!event) continue
            if (event === 'token') full += (data?.text || '')
            else if (event === 'sources') sources = Array.isArray(data?.sources) && data.sources.length ? data.sources : null
            else if (event === 'suggestions') suggestions = Array.isArray(data?.suggestions) && data.suggestions.length ? data.suggestions : null
            else if (event === 'entities') entities = Array.isArray(data?.entities) && data.entities.length ? data.entities as Entity[] : null
            else if (event === 'chart') chart = (data?.chart && typeof data.chart === 'object') ? data.chart as ChartPayload : null
            else if (event === 'error') full = full || 'The assistant hit an error. Please try again.'
          }
        }
        if (!full) full = 'No answer.'
        setMessages(m => [...m, { role: 'assistant', content: '', typing: true }])
        await typewriter(stripMarkdown(full), shown => updateLastAsst({ content: shown }))
        updateLastAsst({ content: full, typing: false, sources, suggestions, entities, chart })
      } else {
        // ---- fallback: plain JSON (also carries sources + suggestions) ----
        const data = await res.json().catch(() => ({} as any))
        let reply: string
        if (res.ok) {
          reply = data.reply || 'No answer.'
          chart = (data.chart && typeof data.chart === 'object') ? data.chart as ChartPayload : null
          entities = Array.isArray(data.entities) && data.entities.length ? data.entities as Entity[] : null
          sources = Array.isArray(data.sources) && data.sources.length ? data.sources : null
          suggestions = Array.isArray(data.suggestions) && data.suggestions.length ? data.suggestions : null
        } else if (res.status === 429) {
          const ra = res.headers.get('Retry-After')
          reply = `You’re sending messages a bit fast${ra ? ` — try again in ${ra}s.` : '. Please slow down.'}`
        } else if (res.status === 413) {
          reply = 'That message is too long — please shorten it.'
        } else {
          reply = data.message || 'The assistant is temporarily unavailable. Please try again.'
        }
        setMessages(m => [...m, { role: 'assistant', content: '', typing: true }])
        await typewriter(stripMarkdown(reply), shown => updateLastAsst({ content: shown }))
        updateLastAsst({ content: reply, typing: false, chart, entities, sources, suggestions })
      }
    } catch {
      setMessages(m => [...m, { role: 'assistant', content: 'Network error — please try again.' }])
    } finally {
      setBusy(false)
    }
  }

  const actions = pageContext ? QUICK_ACTIONS[pageContext.type] : null

  return (
    <div className="fixed bottom-4 right-4 sm:right-6 lg:right-8 z-50 flex flex-col items-end"
         onKeyDown={e => { if (e.key === 'Escape' && open) setOpen(false) }}>
      <style>{FORTI_SWIRL_CSS}</style>
      {open && (
        <div ref={panelRef} className="relative">
          <div aria-hidden className="pointer-events-none absolute -inset-[2px] rounded-[18px] forti-swirl" />
          <div aria-hidden className="pointer-events-none absolute -inset-1 rounded-[22px] opacity-50 blur-lg forti-swirl" />
          <div role="dialog" aria-label="Forti chat"
             className="relative flex h-[32rem] max-h-[calc(100dvh-5rem)] w-[22rem] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-2xl border border-secondary-200 bg-white shadow-2xl animate-slide-up dark:border-secondary-700 dark:bg-secondary-900">
          <header className="flex items-center justify-between border-b border-secondary-200 bg-secondary-50 px-4 py-3 dark:border-secondary-700 dark:bg-secondary-800">
            <div className="flex min-w-0 flex-col">
              {/* Inline height pins the size so the chromatic-gradient wordmark can't flash at its
                  native ~200px before the Tailwind arbitrary class applies (FOUC = the "glitched" look). */}
              <img src="/forti-word-light.png" alt="Forti" style={{ height: 15, width: 'auto' }} className="h-[15px] w-auto select-none dark:hidden" />
              <img src="/forti-word-dark.png" alt="Forti" style={{ height: 15, width: 'auto' }} className="hidden h-[15px] w-auto select-none dark:inline" />
              {pageContext && (
                <span className="truncate text-[11px] text-secondary-500 dark:text-secondary-400" title={pageContext.id}>
                  Viewing {pageContext.type}: {pageContext.label || shortId(pageContext.id)}
                </span>
              )}
            </div>
            <button onClick={() => setOpen(false)} aria-label="Close Forti"
                    className="text-secondary-400 hover:text-secondary-700 dark:hover:text-secondary-200">✕</button>
          </header>
          <div ref={scrollRef} className="flex-1 min-h-0 space-y-3 overflow-y-auto overscroll-contain px-4 py-3" aria-live="polite">
            {messages.map((m, i) => (
              <div key={i} className={'flex min-w-0 ' + (m.role === 'user' ? 'justify-end' : 'flex-col items-start')}>
                <div className={'min-w-0 max-w-[85%] break-words [overflow-wrap:anywhere] rounded-2xl px-3 py-2 text-sm ' +
                  (m.role === 'user'
                    ? 'whitespace-pre-wrap bg-primary-600 text-white'
                    : 'bg-secondary-100 text-secondary-900 dark:bg-secondary-800 dark:text-secondary-100')}>
                  {m.role === 'user' ? truncateAddrs(m.content) : (m.typing ? <TypingLine text={m.content} /> : <AskX1Markdown text={m.content} />)}
                </div>
                {m.role === 'assistant' && m.chart && (
                  <div className="mt-1 w-full min-w-0 overflow-hidden rounded-xl border border-secondary-200 bg-white px-2 py-2 dark:border-secondary-700 dark:bg-secondary-900">
                    <AskX1Chart chart={m.chart} />
                  </div>
                )}
                {m.role === 'assistant' && m.entities && (() => {
                  const ents = m.entities as Entity[]
                  const renderCard = (e: Entity, ei: number) => (
                    <EntityCard key={`${e.id}-${ei}`} entity={e} busy={busy}
                      onNavigate={href => { navigate(href); setOpen(false) }}
                      onQuery={q => send(q)} />
                  )
                  // No groups → flat list, exactly as before (no regression).
                  if (!ents.some(e => e.group)) {
                    return <div className="w-full min-w-0">{ents.map(renderCard)}</div>
                  }
                  // Grouped (e.g. top validators) → each group its own labeled block ("bubble").
                  const order: string[] = []
                  const byGroup: Record<string, Entity[]> = {}
                  for (const e of ents) {
                    const g = e.group || 'More'
                    if (!byGroup[g]) { byGroup[g] = []; order.push(g) }
                    byGroup[g].push(e)
                  }
                  return (
                    <div className="w-full min-w-0 space-y-2">
                      {order.map(g => (
                        <div key={g} className="rounded-xl border border-secondary-200 bg-secondary-50/60 p-1.5 dark:border-secondary-700 dark:bg-secondary-800/40">
                          <div className="px-1 pb-1 text-[11px] font-semibold text-secondary-600 dark:text-secondary-300">{g}</div>
                          {byGroup[g].map(renderCard)}
                        </div>
                      ))}
                    </div>
                  )
                })()}
                {m.role === 'assistant' && m.sources && m.sources.length > 0 && (
                  <div className="mt-1 flex flex-wrap items-center gap-1 text-[10px] text-secondary-400">
                    <span className="font-medium">Sources:</span>
                    {m.sources.map((s, si) => (
                      <span key={si} className="rounded bg-secondary-100 px-1.5 py-0.5 dark:bg-secondary-800">{s.label}</span>
                    ))}
                  </div>
                )}
                {m.role === 'assistant' && m.suggestions && m.suggestions.length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {m.suggestions.map((q, qi) => (
                      <button key={qi} onClick={() => send(q)} disabled={busy}
                        className="rounded-full border border-primary-300 bg-primary-50 px-2.5 py-1 text-xs text-primary-700 transition hover:bg-primary-100 disabled:opacity-50 dark:border-primary-700 dark:bg-primary-900/30 dark:text-primary-300 dark:hover:bg-primary-900/50">
                        {q}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {messages.length === 1 && !busy && trending.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5" aria-label="Trending searches">
                <span className="text-[11px] font-medium text-secondary-400">🔥 Trending</span>
                {trending.map((q, i) => (
                  <button key={i} onClick={() => send(q)} disabled={busy}
                    className="rounded-full border border-secondary-300 bg-secondary-50 px-2.5 py-1 text-xs text-secondary-700 transition hover:border-primary-400 hover:text-primary-700 disabled:opacity-50 dark:border-secondary-600 dark:bg-secondary-800 dark:text-secondary-200 dark:hover:border-primary-500 dark:hover:text-primary-300">
                    {q.charAt(0).toUpperCase() + q.slice(1)}
                  </button>
                ))}
              </div>
            )}
            {busy && messages[messages.length - 1]?.role === 'user' && <StreamLoader />}
          </div>
          {actions && (
            <div className="flex flex-wrap gap-1.5 border-t border-secondary-200 px-3 py-2 dark:border-secondary-700" aria-label="Quick actions">
              {actions.map(a => (
                <button key={a} onClick={() => send(a)} disabled={busy}
                  className="rounded-full border border-secondary-300 bg-secondary-50 px-2.5 py-1 text-xs text-secondary-700 transition hover:border-primary-400 hover:text-primary-700 disabled:opacity-50 dark:border-secondary-600 dark:bg-secondary-800 dark:text-secondary-200 dark:hover:border-primary-500 dark:hover:text-primary-300">
                  {a}
                </button>
              ))}
            </div>
          )}
          <div className="border-t border-secondary-200 p-2 dark:border-secondary-700">
            <div className="flex items-end gap-2">
              <textarea ref={inputRef} value={input} onChange={e => setInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send() } }}
                rows={1} maxLength={4000} placeholder="Ask Forti anything…" aria-label="Message"
                className="max-h-24 flex-1 resize-none rounded-pill border border-secondary-300 bg-white px-3 py-2 text-sm text-secondary-900 outline-none focus:border-primary-500 dark:border-secondary-600 dark:bg-secondary-800 dark:text-secondary-100" />
              <button onClick={() => send()} disabled={busy || !input.trim()}
                className="rounded-pill bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50">Send</button>
            </div>
            <p className="mt-1 px-1 text-[10px] text-secondary-400">Read-only assistant · explains on-chain data, not financial advice.</p>
          </div>
          </div>
        </div>
      )}
    </div>
  )
}
